import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ms } from "date-fns/locale";
import { AlertTriangle, CheckCircle2, MonitorPlay } from "lucide-react";
import api from "@/lib/api";
import { AUTO_BG_MIN_VERSION, getAutopilot, helperVersion, inQuiet, versionAtLeast } from "@/lib/helper";
import BgProgress from "@/components/BgProgress";

const ago = (d) => formatDistanceToNow(new Date(d), { addSuffix: true, locale: ms });

function pill(auto, now) {
  if (!auto) return { label: "Tidak dikesan", cls: "bg-slate-500/20 text-slate-300" };
  if (!auto.enabled) return { label: "MATI", cls: "bg-slate-500/20 text-slate-300" };
  if (auto.errors > 0) return { label: "AKTIF · CUBA SEMULA", cls: "bg-amber-500/20 text-amber-300" };
  if (auto.running) return { label: "AKTIF · SEDANG POST", cls: "bg-emerald-500/20 text-emerald-300 animate-pulse" };
  if (inQuiet(auto.quiet, new Date(now))) return { label: `AKTIF · WAKTU REHAT HINGGA ${auto.quiet.end}`, cls: "bg-indigo-500/20 text-indigo-300" };
  if (auto.nextRunAt > now) return { label: `AKTIF · SETERUSNYA ${Math.ceil((auto.nextRunAt - now) / 1000)}s`, cls: "bg-emerald-500/20 text-emerald-300" };
  return { label: "AKTIF · MENUNGGU GILIRAN", cls: "bg-emerald-500/20 text-emerald-300" };
}

export default function BgStatusCard() {
  const live = versionAtLeast(helperVersion(), AUTO_BG_MIN_VERSION);
  const [srv, setSrv] = useState(null);
  const [auto, setAuto] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const loadSrv = () => api.get("/helper/bg-status", { params: { tz_offset: new Date().getTimezoneOffset() } }).then((r) => setSrv(r.data)).catch(() => {});
    const loadAuto = () => live && getAutopilot().then(setAuto);
    loadSrv(); loadAuto();
    const a = setInterval(loadSrv, 15000);
    const b = setInterval(() => { loadAuto(); setNow(Date.now()); }, 3000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [live]);

  const p = pill(auto, now);
  const limit = auto?.dailyLimit;
  const liveErr = auto?.enabled && auto?.errors > 0 ? auto.lastReason : null;
  const lastErr = srv?.last_error;

  return (
    <section className="panel fade-up mt-6 p-5" data-testid="dash-bg-status">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <MonitorPlay className="h-5 w-5 text-indigo-300" />
          <h2 className="font-bold">Mod Latar Chrome</h2>
          <span className={`rounded px-2 py-0.5 font-mono text-[11px] ${p.cls}`} data-testid="dash-bg-status-pill">{p.label}</span>
        </div>
        <Link to="/jadual" className="text-sm text-primary hover:underline" data-testid="dash-bg-status-manage">Urus Mod Latar →</Link>
      </div>
      {!live && <p className="mt-2 text-xs text-muted-foreground" data-testid="dash-bg-status-nohelper">Status langsung hanya kelihatan di Chrome yang dipasang Helper v{AUTO_BG_MIN_VERSION}+. Kiraan di bawah dari server.</p>}
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div>
          <p className="eyebrow">Dipost hari ini</p>
          <p className="mt-1 font-mono text-3xl" data-testid="dash-bg-today">{srv?.today_done ?? "–"}{limit > 0 && <span className="text-base text-muted-foreground"> / {limit}</span>}</p>
          {srv?.today_failed > 0 && <p className="text-xs text-red-300" data-testid="dash-bg-today-failed">{srv.today_failed} gagal hari ini</p>}
        </div>
        <div className="min-w-0">
          <p className="eyebrow">Terakhir berjaya</p>
          {srv?.last_done ? (
            <p className="mt-1 flex items-start gap-1.5 text-sm" data-testid="dash-bg-last-done"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /><span className="min-w-0"><b className="block truncate">{srv.last_done.group_name}</b><span className="text-xs text-muted-foreground">{ago(srv.last_done.at)}</span></span></p>
          ) : <p className="mt-1 text-sm text-muted-foreground" data-testid="dash-bg-last-done">Belum ada</p>}
        </div>
        <div className="min-w-0">
          <p className="eyebrow">Ralat terakhir</p>
          {liveErr ? (
            <p className="mt-1 flex items-start gap-1.5 text-sm text-amber-200" data-testid="dash-bg-last-error"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span className="min-w-0 break-words">{liveErr} <span className="block text-xs text-muted-foreground">Cuba semula {auto.errors}x · masih aktif</span></span></p>
          ) : lastErr ? (
            <p className="mt-1 flex items-start gap-1.5 text-sm text-red-200" data-testid="dash-bg-last-error"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span className="min-w-0"><b className="block truncate">{lastErr.group_name}</b><span className="block break-words text-xs">{lastErr.error}</span><span className="text-xs text-muted-foreground">{ago(lastErr.at)}</span></span></p>
          ) : <p className="mt-1 text-sm text-muted-foreground" data-testid="dash-bg-last-error">Tiada ralat</p>}
        </div>
      </div>
      <BgProgress srv={srv} auto={auto} now={now} />
    </section>
  );
}
